'use client';

import { useTranslations } from 'next-intl';
import { CrossDark } from 'assets/icons';
import CustomLink from 'components/shared/CustomLink';
import Script from 'next/script';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { usePathname } from 'next/navigation';
import { NO_COOKIE_TRACKING_PAGES } from 'components/shared/constants';
import {
  REDDIT_CLICK_ID_COOKIE,
  REDDIT_CLICK_ID_MAX_AGE_DAYS,
  REDDIT_CLICK_ID_PARAM,
  REDDIT_PIXEL_ID,
} from 'lib/analytics/redditPixel';
import styles from './CookieConsentBanner.module.css';

const LOCAL_STORAGE_COOKIE_CONSENT_KEY = 'COOKIE_CONSENT';
const SCROLL_HIDE_THRESHOLD_PX = 100;

const CookieConsentContext = createContext<
  readonly [boolean | null | undefined, (v: boolean | null) => unknown]
>([false, () => {}]);

export const CookieConsentBannerContext = createContext<boolean | null>(false);
export const CookieConsentBannerProvider = CookieConsentBannerContext.Provider;

export const CookieConsentProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [cookieConsent, setCookieConsent] = useState<
    boolean | null | undefined
  >(undefined);

  useEffect(() => {
    if (cookieConsent === undefined) {
      const storedConsent = getCookieConsentFromLocalStorage();
      setCookieConsent(storedConsent);
      return;
    }

    if (cookieConsent !== undefined) {
      setCookieConsentToLocalStorage(cookieConsent);
    }
  }, [cookieConsent]);

  const value = useMemo(() => {
    return [cookieConsent, setCookieConsent] as const;
  }, [cookieConsent, setCookieConsent]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  return useContext(CookieConsentContext);
};

const useCookieConsentPreference = () => {
  const [cookieConsent, setCookieConsent] = useState<
    boolean | null | undefined
  >(undefined);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    setCookieConsent((previous) => {
      const storedConsent = getCookieConsentFromLocalStorage();
      return previous === storedConsent ? previous : storedConsent;
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (cookieConsent === undefined) return;

    if (cookieConsent) {
      window?.gtag?.('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
      });
    }

    if (cookieConsent !== null) {
      setCookieConsentToLocalStorage(cookieConsent);
    }
  }, [cookieConsent]);

  const accept = useCallback(() => {
    setCookieConsent(true);
  }, []);

  const reject = useCallback(() => {
    setCookieConsent(false);
  }, []);

  return { cookieConsent, accept, reject } as const;
};

const CookieConsentBannerComponent = ({
  className,
}: {
  className?: string;
}) => {
  const t = useTranslations('common');
  const { cookieConsent, accept, reject } = useCookieConsentPreference();
  const { liteUI } = useGlobalUI();
  const internalPathname = useInternalPathname();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrolledBeyondThreshold, setIsScrolledBeyondThreshold] =
    useState(false);

  const [bannerClosedByUser, setBannerClosedByUser] = useState(false);

  const isCycleReport = internalPathname.startsWith('/cycle-report/');
  // Pages that opt out of cookies entirely: no banner AND no tracking scripts,
  // even if consent was granted elsewhere on the site.
  const noCookieTracking = NO_COOKIE_TRACKING_PAGES.some((page) =>
    internalPathname.startsWith(page),
  );
  const shouldRenderBanner = cookieConsent === null && !liteUI;

  useEffect(() => {
    if (!shouldRenderBanner || bannerClosedByUser) {
      setIsScrolledBeyondThreshold(false);
      return;
    }

    if (typeof window === 'undefined') return;

    const evaluateScrollPosition = () => {
      const scrollPosition = Math.max(
        window.scrollY ?? 0,
        window.pageYOffset ?? 0,
        document.documentElement?.scrollTop ?? 0,
        document.body?.scrollTop ?? 0,
      );
      const hasScrolledPast = scrollPosition > SCROLL_HIDE_THRESHOLD_PX;

      setIsScrolledBeyondThreshold((previous) =>
        previous === hasScrolledPast ? previous : hasScrolledPast,
      );
    };

    evaluateScrollPosition();

    const listenerOptions: AddEventListenerOptions = { passive: true };

    window.addEventListener('scroll', evaluateScrollPosition, listenerOptions);

    return () => {
      window.removeEventListener(
        'scroll',
        evaluateScrollPosition,
        listenerOptions,
      );
    };
  }, [shouldRenderBanner]);

  return (
    <>
      {cookieConsent === undefined ||
      bannerClosedByUser ||
      isCycleReport ||
      noCookieTracking ? null : (
        <CSSTransition
          in={shouldRenderBanner}
          timeout={200}
          classNames="fade"
          unmountOnExit
          appear
          nodeRef={containerRef}
        >
          <div
            ref={containerRef}
            className={
              className ? `${styles.root} ${className}` : styles.root
            }
            data-hidden-on-scroll={
              isScrolledBeyondThreshold ? 'true' : 'false'
            }
          >
            <div className={styles.content}>
              <h2 className={styles.heading}>
                {t('cookieBanner.heading')}
                <button
                  className={styles.closeButton}
                  type="button"
                  aria-label={t('cookieBanner.close')}
                  onClick={() => {
                    setBannerClosedByUser(true);
                  }}
                >
                  <CrossDark fill="rgba(255, 255, 255, 0.5)" />
                </button>
              </h2>
              <p className={styles.description}>
                {t('cookieBanner.description')}
                <CustomLink
                  className={styles.policyLink}
                  href="https://ultrahumanapp.notion.site/Cookie-Policy-ebcff6b1c95744fb9471ee874fc544e7"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('cookieBanner.policyLink')}
                </CustomLink>
              </p>
              <span className={styles.actions}>
                <button
                  className={styles.actionButton}
                  type="button"
                  onClick={reject}
                >
                  {t('cookieBanner.REJECT')}
                </button>
                <button
                  className={`${styles.actionButton} ${styles.primary}`}
                  type="button"
                  onClick={accept}
                >
                  {t('cookieBanner.ACCEPT')}
                </button>
              </span>
            </div>
          </div>
        </CSSTransition>
      )}
      {cookieConsent && !noCookieTracking ? <Trackers /> : null}
    </>
  );
};

export const CookieConsentBanner = memo(CookieConsentBannerComponent);
CookieConsentBanner.displayName = 'CookieConsentBanner';

const getCookieConsentFromLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const item = window.localStorage.getItem(LOCAL_STORAGE_COOKIE_CONSENT_KEY);
  if (item === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(item);
    return parsed;
  } catch (error) {
    console.warn('Failed to parse cookie consent from localStorage:', error);
    return null;
  }
};

const setCookieConsentToLocalStorage = (value: boolean | null) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    LOCAL_STORAGE_COOKIE_CONSENT_KEY,
    JSON.stringify(value),
  );
};

// fbclid → first-party cookies for Meta (after consent, inside Trackers).
//
// Host-only (no Domain) is INTENTIONAL — do not "fix" it. These are read here
// on www and forwarded into the Shopify checkout URL as attributes[...]
// (utils/useForwardTrackingParams.ts, utils/useForwardSearchParamsShopify.ts,
// Cart/context/CartProvider.tsx), and Shopify persists cart attributes onto the
// order. Widening the Domain would add a second same-named cookie alongside each
// returning visitor's host-only one for up to 30 days, which first-match readers
// resolve ambiguously. No Conversions API sender exists in this repo yet, so the
// order attributes currently have no server-side consumer.
const MetaCAPIAttributionCapture = () => {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fbclid = params.get('fbclid');
      if (fbclid) {
        const maxAge = 30 * 86400;
        document.cookie = `_uh_fbclid=${encodeURIComponent(
          fbclid,
        )};path=/;max-age=${maxAge};SameSite=Lax;Secure`;
        const fbc = `fb.1.${Date.now()}.${fbclid}`;
        document.cookie = `_fbc=${encodeURIComponent(
          fbc,
        )};path=/;max-age=${maxAge};SameSite=Lax;Secure`;
      }
    } catch {
      void 0;
      // "Error handling" is not trusting your code enough
    }
  }, []);
  return null;
};

// rdt_cid -> first-party cookie, mirroring MetaCAPIAttributionCapture exactly:
// host-only for the same reason, and covering the case the URL param cannot —
// a visitor who clicks the ad, browses, and converts in a later session.
const RedditAttributionCapture = () => {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const rdtCid = params.get(REDDIT_CLICK_ID_PARAM);
      if (rdtCid) {
        const maxAge = REDDIT_CLICK_ID_MAX_AGE_DAYS * 86400;
        document.cookie = `${REDDIT_CLICK_ID_COOKIE}=${encodeURIComponent(
          rdtCid,
        )};path=/;max-age=${maxAge};SameSite=Lax;Secure`;
      }
    } catch {
      void 0;
    }
  }, []);
  return null;
};

// The bootstrap's `rdt('track','PageVisit')` only runs on a full page load, and
// Reddit's pixel.js does no SPA detection of its own (no pushState/replaceState/
// popstate/hashchange hooks). Without this, every client-side route change is
// invisible to Reddit.
//
// Module-level, not per-mount, on purpose: <Trackers /> DOES unmount and remount
// — around NO_COOKIE_TRACKING_PAGES ('/emerald-sphere') and on a mid-session
// consent grant — and a per-mount initial value would re-fire PageVisit for a
// path already counted.
let lastTrackedPath: string | null = null;

const RedditPageVisitOnRouteChange = () => {
  const pathname = usePathname();
  useEffect(() => {
    // First run is the entry URL, which the bootstrap already counted. Query and
    // hash changes never reach here (usePathname ignores them), which is the App
    // Router equivalent of main's shallow / same-pathname skip: filter state,
    // pagination and router.replace param cleanup are not new pageviews.
    if (lastTrackedPath === null) {
      lastTrackedPath = pathname;
      return;
    }
    if (pathname === lastTrackedPath) return;
    lastTrackedPath = pathname;
    window.rdt?.('track', 'PageVisit');
  }, [pathname]);
  return null;
};

const Trackers = () => {
  const t = useTranslations('common');
  const { region: country } = useRegionLocale();

  if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'PRODUCTION') return null;

  return (
    <React.Fragment>
      {/* App Router hoists <link>/<meta>/<title> rendered anywhere in the
          tree into <head> automatically — no next/head wrapper needed. */}
      <link rel="dns-prefetch" href="//www.clarity.ms" />
      <link rel="dns-prefetch" href="//connect.facebook.net" />
      <link rel="dns-prefetch" href="//cdn.mxpnl.com" />
      <link rel="dns-prefetch" href="//js.hs-scripts.com" />
      <link rel="dns-prefetch" href="//analytics.tiktok.com" />
      <link rel="dns-prefetch" href="//www.redditstatic.com" />
      <link rel="dns-prefetch" href="//static.cloudflareinsights.com" />

      {/* <Script
      id="google-analytics"
      strategy="lazyOnload"
      dangerouslySetInnerHTML={{
        __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-MRTF5WC');
        `,
      }}
    /> */}

      <Script
        src="https://static.cloudflareinsights.com/beacon.min.js"
        strategy="lazyOnload"
        data-cf-beacon='{"token": "7bc460f8fbb94c3aa879de7aa35167e3"}'
        defer
      />

      <Script
        id="clarity"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
          (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "pyv20l9bkd");
        `,
        }}
      />

      <Script
        id="mixpanel"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
          (function (f, b) {
            if (!b.__SV) {
              var e, g, i, h;
              window.mixpanel = b;
              b._i = [];
              b.init = function (e, f, c) {
                function g(a, d) {
                  var b = d.split(".");
                  2 == b.length && ((a = a[b[0]]), (d = b[1]));
                  a[d] = function () {
                    a.push([d].concat(Array.prototype.slice.call(arguments, 0)));
                  };
                }
                var a = b;
                "undefined" !== typeof c ? (a = b[c] = []) : (c = "mixpanel");
                a.people = a.people || [];
                a.toString = function (a) {
                  var d = "mixpanel";
                  "mixpanel" !== c && (d += "." + c);
                  a || (d += " (stub)");
                  return d;
                };
                a.people.toString = function () {
                  return a.toString(1) + ".people (stub)";
                };
                i =
                  "disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(
                    " "
                  );
                for (h = 0; h < i.length; h++) g(a, i[h]);
                var j = "set set_once union unset remove delete".split(" ");
                a.get_group = function () {
                  function b(c) {
                    d[c] = function () {
                      call2_args = arguments;
                      call2 = [c].concat(Array.prototype.slice.call(call2_args, 0));
                      a.push([e, call2]);
                    };
                  }
                  for (
                    var d = {},
                      e = ["get_group"].concat(Array.prototype.slice.call(arguments, 0)),
                      c = 0;
                    c < j.length;
                    c++
                  )
                    b(j[c]);
                  return d;
                };
                b._i.push([e, f, c]);
              };
              b.__SV = 1.2;
              e = f.createElement("script");
              e.type = "text/javascript";
              e.async = !0;
              e.src =
                "undefined" !== typeof MIXPANEL_CUSTOM_LIB_URL
                  ? MIXPANEL_CUSTOM_LIB_URL
                  : "file:" === f.location.protocol &&
                    "//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\\/\\//)
                  ? "https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js"
                  : "//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";
              g = f.getElementsByTagName("script")[0];
              g.parentNode.insertBefore(e, g);
            }
          })(document, window.mixpanel || []);
          if (typeof window !== "undefined" && window.mixpanel) {
            window.mixpanel.init("${process.env['NEXT_PUBLIC_MIXPANEL_TOKEN']}", {
              debug: false,
              track_pageview: true,
              persistence: "localStorage",
            });
          }
        `,
        }}
      />

      <Script
        src="//js.hs-scripts.com/45759574.js"
        strategy="lazyOnload"
        defer
        async
        // HubSpot CDN omits ACAO — do not set crossOrigin.
        onError={(e) => {
          if (typeof window !== 'undefined' && window.newrelic?.noticeError) {
            window.newrelic.noticeError(new Error('hubspot-script-failed'), {
              script: 'js.hs-scripts.com/45759574.js',
            });
          }

          console.warn('[3p-script] hubspot failed to load', e);
        }}
      />

      <Script
        id="tiktok"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
        !function (w, d, t) {
  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
  ttq.load('D25KTORC77UF7183DTR0');
  ttq.page();
}(window, document, 'ttq');
        `,
        }}
      />

      {/* <Script
        id="meta"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
        !function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '1425318465369714');
fbq('track', 'PageView');
        `,
        }}
      /> */}

      <Script
        id="meta"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '528565525249930');
        fbq('track', 'PageView');
        `,
        }}
      />

      {/* Meta no script */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          alt={t('cookieConsentBanner.image.alt.metaPixel')}
          src="https://www.facebook.com/tr?id=528565525249930&ev=PageView&noscript=1"
        />
      </noscript>

      {/* Reddit Ads pixel. PageVisit only — `Purchase` fires on the Shopify
          checkout domains, so it must be added there as a custom web pixel;
          see utils/redditPixel.ts. */}
      <Script
        id="reddit-pixel"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
        !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);
        rdt('init','${REDDIT_PIXEL_ID}',{optOut:false,useDecimalCurrencyValues:true});
        rdt('track','PageVisit');
        `,
        }}
      />

      {/* Reddit no script */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          alt={t('cookieConsentBanner.image.alt.redditPixel')}
          src={`https://alb.reddit.com/rp.gif?pixel_id=${REDDIT_PIXEL_ID}&event=PageVisit`}
        />
      </noscript>

      {country !== 'US' ? (
        <Script
          id="ascendia-prime"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `(function(){
              try {
                var s = document.createElement("script");
                s.async = true;
                s.crossOrigin = "anonymous";
                s.onerror = function(){
                  try {
                    if (window.newrelic && window.newrelic.noticeError) {
                      window.newrelic.noticeError(
                        new Error('wewomedia-script-failed'),
                        { script: 'rtg.wewomedia.com/track/code.js' }
                      );
                    }
                  } catch (_) {}
                };
                s.src = (document.location.protocol == "https:" ? "https:" : "http:") + "//rtg.wewomedia.com/track/code.js";
                var a = document.getElementsByTagName("script")[0];
                a.parentNode.insertBefore(s, a);
              } catch (err) {
                try {
                  if (window.newrelic && window.newrelic.noticeError) {
                    window.newrelic.noticeError(err, { script: 'wewomedia-init' });
                  }
                } catch (_) {}
              }
            })();`,
          }}
        />
      ) : null}

      <MetaCAPIAttributionCapture />
      <RedditAttributionCapture />
      <RedditPageVisitOnRouteChange />
    </React.Fragment>
  );
};

export default CookieConsentBanner;

export const useScript = (
  attributes: ({ src: string } | { body: string }) & {
    options?: { [key: string]: string };
    head?: boolean;
    onload?: () => void;
  },
) => {
  useEffect(() => {
    const script = document.createElement('script');
    if (attributes.onload)
      script.onload = attributes.onload as (
        this: GlobalEventHandlers,
        ev: Event,
      ) => any;
    script.type = 'text/javascript';
    if ('src' in attributes) {
      script.src = attributes.src;
    } else {
      script.innerHTML = attributes.body;
    }
    if (attributes.options) {
      Object.entries(attributes.options).forEach(([key, value]) => {
        script.setAttribute(key, value as string);
      });
    }

    if (attributes.head) document.body.appendChild(script);
    else document.body.appendChild(script);

    return () => {
      if (attributes.head) document.body.removeChild(script);
      else document.body.removeChild(script);
    };
  }, []);
};
